import {
  Box,
  Checkbox,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CategoryMultiSelect, { MenuProps } from 'components/common/Select/CategoryMultiSelect';
import CountrySelect, { ICountryAutoComplete } from 'components/common/Select/CountrySelect';
import { ISearchOpp, OpportunityLocationType } from 'global/interfaces/opportunity';
import { StyledTextField } from './OppFilters';
import { RoundButton } from 'components/common/Button/RoundButton';
import { FormikProps } from 'formik';
import { Link } from 'react-router-dom';
import { nameof } from 'ts-simple-nameof';

interface IFilterProps {
  form: FormikProps<ISearchOpp>;
  onDrawerClose?: () => void;
  children: React.ReactNode;
}

export function GigFilterForm({ form, onDrawerClose, children }: IFilterProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleCountryChange = (_: React.SyntheticEvent, value: ICountryAutoComplete | null): void => {
    const countryCode: string = value?.code ?? '';
    form.setFieldValue(
      nameof<ISearchOpp>(x => x.countries),
      value ? [countryCode] : [],
      true,
    );
  };

  return (
    <Stack
      sx={{ mb: !isMobile ? 5 : 0, mt: !isMobile ? 5 : 4 }}
      spacing={2}
      direction={'column'}
      component="form"
      onSubmit={form.handleSubmit}
    >
      <Stack justifyContent="space-between" direction="row">
        <Typography variant="subtitle1" mb={0.25}>
          Filter gigs
        </Typography>
        <Link
          to="https://help.shoutt.co/en/articles/4032002"
          target="_blank"
          style={{
            color: theme.palette.primary[600],
            textDecoration: 'none',
            fontSize: theme.typography.caption.fontSize,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          Help
        </Link>
      </Stack>
      <CountrySelect
        value={form.values.countries && form.values.countries[0] ? form.values.countries[0] : undefined}
        label="Countries"
        size={'small'}
        onChange={handleCountryChange}
      />
      <FormControl fullWidth>
        <InputLabel id="multiple-category-label" size="small">
          Role Types
        </InputLabel>
        <Select
          labelId="multiple-role-types-label"
          multiple
          name="roleTypes"
          value={form.values.roleTypes}
          size="small"
          displayEmpty
          onChange={form.handleChange}
          input={<OutlinedInput id="select-multiple-chip" label="Role Types" />}
          renderValue={selected => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map(value => (
                <Chip key={value} label={value !== OpportunityLocationType.Local ? value : 'On-site'} size="small" />
              ))}
            </Box>
          )}
          MenuProps={MenuProps}
        >
          <MenuItem value={OpportunityLocationType.Remote}>
            <Checkbox
              checked={form.values.roleTypes ? form.values.roleTypes?.includes(OpportunityLocationType.Remote) : false}
            />
            <ListItemText primary="Remote" />
          </MenuItem>
          <MenuItem value={OpportunityLocationType.Hybrid}>
            <Checkbox
              checked={form.values.roleTypes ? form.values.roleTypes?.includes(OpportunityLocationType.Hybrid) : false}
            />
            <ListItemText primary="Hybrid" />
          </MenuItem>
          <MenuItem value={OpportunityLocationType.Local}>
            <Checkbox
              checked={form.values.roleTypes ? form.values.roleTypes?.includes(OpportunityLocationType.Local) : false}
            />
            <ListItemText primary="On-site" />
          </MenuItem>
        </Select>
      </FormControl>
      <CategoryMultiSelect
        value={form.values.categories ? [...form.values.categories] : []}
        homeCategoriesOnly={false}
        label="Categories"
        onChange={changedCategories =>
          form.setFieldValue(
            nameof<ISearchOpp>(x => x.categories),
            changedCategories.target.value,
          )
        }
        input={<OutlinedInput id="select-multiple-chip" label="Categories" />}
        error={form.touched.categories !== undefined && Boolean(form.errors.categories)}
        helpertext={form.errors.categories ?? ''}
        size="small"
      />
      <StyledTextField
        name="search"
        label="Keywords"
        value={form.values.search}
        onChange={form.handleChange}
        autoComplete="off"
        placeholder="PPC, Paid Media, Google Ads"
        size={'small'}
        fullWidth
        InputProps={{
          autoComplete: 'off',
        }}
      />
      <Grid container>
        <Grid item xs={12}>
          <Typography alignItems={'center'} display={'flex'} ml={-1.5} mt={-1}>
            <Checkbox
              onChange={form.handleChange}
              name={nameof<ISearchOpp>(x => x.free)}
              checked={form.values.free}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 24 } }}
            />
            Free to apply only
          </Typography>
        </Grid>
      </Grid>
      <Grid container gap={2} justifyContent="space-between">
        {isMobile && (
          <Grid item xs={12}>
            <RoundButton variant="outlined" onClick={onDrawerClose} sx={{ marginTop: '-4px', width: '100%' }}>
              Cancel
            </RoundButton>
          </Grid>
        )}
        <Grid item xs={12}>
          <RoundButton variant="contained" type="submit" sx={{ marginTop: '-4px', width: '100%' }}>
            Update Results
          </RoundButton>
        </Grid>
      </Grid>
      {children}
    </Stack>
  );
}
